import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { FloatingMenu, type FloatingMenuPlacement } from './FloatingMenu'
import { OverflowTip } from './Hover/OverflowTip'
import { Button } from './Button'
import { Pressable } from './Pressable'
import { resolveHourCycleLocale } from './timePickerHelpers'
import { useNls } from '../../nls/nlsProvider'
import { getNlsString } from '../../nls/nlsRuntime'

/* ---------------------------------------------------------------------------
 * Helpers
 * ---------------------------------------------------------------------------*/

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n))

/** Parse a "HH:mm" string. Returns { h24, m } or null on invalid/empty. */
function parseTime(value: string | null | undefined): { h24: number; m: number } | null {
  if (!value) return null
  const m = value.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return null
  const h24 = Number(m[1])
  const min = Number(m[2])
  if (h24 < 0 || h24 > 23 || min < 0 || min > 59) return null
  return { h24, m: min }
}

/** Format { h24, m } as "HH:mm". */
function formatTime(h24: number, m: number): string {
  return `${pad2(h24)}:${pad2(m)}`
}

/**
 * Round minutes to nearest multiple of step, carrying into hours if needed.
 * Returns { h24, m } — both normalised to 0..23 and 0..59.
 */
function roundNow(rawH24: number, rawM: number, step: number): { h24: number; m: number } {
  const totalMinutes = rawH24 * 60 + rawM
  const rounded = Math.round(totalMinutes / step) * step
  const h24 = Math.floor(rounded / 60) % 24
  const m = rounded % 60
  return { h24, m }
}



/* ---------------------------------------------------------------------------
 * Props
 * ---------------------------------------------------------------------------*/

export type TimePickerProps = {
  /** "HH:mm" in 24-h, or '' / undefined / null for no selection. */
  value: string | null | undefined
  /** Emits "HH:mm" in 24-h, or '' when cleared. */
  onChange: (value: string) => void
  disabled?: boolean
  readOnly?: boolean
  placeholder?: string
  ariaLabel?: string
  className?: string
  id?: string
  name?: string
  size?: 'sm' | 'md'
  clearable?: boolean
  placement?: FloatingMenuPlacement
  style?: CSSProperties
  onClose?: () => void
  /** Minutes between each option in the minute column. Default 5. */
  minuteStep?: number
}

/* ---------------------------------------------------------------------------
 * TimePicker
 * ---------------------------------------------------------------------------*/

export function TimePicker({
  value,
  onChange,
  disabled = false,
  readOnly = false,
  placeholder,
  ariaLabel,
  className,
  id,
  name,
  size,
  clearable = true,
  placement,
  style,
  onClose,
  minuteStep = 5,
}: TimePickerProps) {
  const { locale } = useNls()

  // SSR-safe browser language (same guard as nlsProvider.tsx:30-32)
  const browserLang = typeof navigator !== 'undefined' ? navigator.language : undefined

  const is12h = useMemo(
    () =>
      new Intl.DateTimeFormat(
        resolveHourCycleLocale(locale, browserLang),
        { hour: 'numeric' },
      ).resolvedOptions().hour12,
    [locale, browserLang],
  )

  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)

  // Draft state — only committed when the user presses Done.
  const parsed = parseTime(value)
  const [draftH24, setDraftH24] = useState<number>(parsed?.h24 ?? 0)
  const [draftM, setDraftM] = useState<number>(parsed?.m ?? 0)
  // AM/PM for 12h mode: 0 = AM, 1 = PM
  const [draftAmPm, setDraftAmPm] = useState<0 | 1>(() => (parsed != null ? (parsed.h24 >= 12 ? 1 : 0) : 0))

  const [prevValue, setPrevValue] = useState(value)
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevValue !== value || prevOpen !== open) {
    const valueChanged = prevValue !== value
    const justOpened = prevOpen !== open && open
    setPrevValue(value)
    setPrevOpen(open)

    if (valueChanged || justOpened) {
      const p = parseTime(value)
      if (p) {
        setDraftH24(p.h24)
        setDraftM(p.m)
        setDraftAmPm(p.h24 >= 12 ? 1 : 0)
      } else if (justOpened) {
        setDraftH24(0)
        setDraftM(0)
        setDraftAmPm(0)
      }
    }
  }

  const handleOpen = useCallback(() => {
    if (disabled || readOnly) return
    setOpen(true)
  }, [disabled, readOnly])

  const handleClose = useCallback(() => {
    setOpen(false)
    onClose?.()
  }, [onClose])

  const handleToggle = useCallback(() => {
    if (open) handleClose()
    else handleOpen()
  }, [open, handleOpen, handleClose])

  const handleDone = useCallback(() => {
    let h = draftH24
    if (is12h) {
      // Map 12h display back to 24h
      const display = h % 12 // 0..11
      h = draftAmPm === 1 ? display + 12 : display
    }
    onChange(formatTime(h, draftM))
    handleClose()
  }, [draftH24, draftM, draftAmPm, is12h, onChange, handleClose])

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange('')
      handleClose()
    },
    [onChange, handleClose],
  )

  const handleNow = useCallback(() => {
    const now = new Date()
    const { h24, m } = roundNow(now.getHours(), now.getMinutes(), minuteStep)
    setDraftH24(h24)
    setDraftM(m)
    setDraftAmPm(h24 >= 12 ? 1 : 0)
  }, [minuteStep])

  // Scroll selected cell into view on open
  const hourColRef = useRef<HTMLDivElement | null>(null)
  const minColRef = useRef<HTMLDivElement | null>(null)
  const ampmColRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const scrollSelected = (ref: React.RefObject<HTMLDivElement | null>) => {
      const col = ref.current
      if (!col) return
      const sel = col.querySelector<HTMLElement>('.dsTime__cell.is-selected')
      if (sel) sel.scrollIntoView({ block: 'nearest' })
    }
    // rAF gives the popover time to mount
    const id = requestAnimationFrame(() => {
      scrollSelected(hourColRef)
      scrollSelected(minColRef)
      scrollSelected(ampmColRef)
    })
    return () => cancelAnimationFrame(id)
  }, [open])

  // Trigger label
  const labelText = useMemo(() => {
    const p = parseTime(value)
    if (!p) return null
    if (is12h) {
      const h12 = p.h24 % 12 === 0 ? 12 : p.h24 % 12
      const suffix = p.h24 >= 12
        ? getNlsString('NLSCommonTimePickerPm')
        : getNlsString('NLSCommonTimePickerAm')
      return `${pad2(h12)}:${pad2(p.m)} ${suffix}`
    }
    return formatTime(p.h24, p.m)
  }, [value, is12h])

  const resolvedPlaceholder = placeholder ?? getNlsString('NLSCommonTimePickerPlaceholder')
  const triggerText = labelText ?? resolvedPlaceholder
  const showClear = clearable && labelText != null && !disabled && !readOnly

  // Hours column: 24h → 0..23, 12h → 1..12
  const hours = useMemo(() => {
    if (is12h) return Array.from({ length: 12 }, (_, i) => i + 1) // 1..12
    return Array.from({ length: 24 }, (_, i) => i) // 0..23
  }, [is12h])

  // Minutes column
  const minutes = useMemo(() => {
    const steps: number[] = []
    for (let m = 0; m < 60; m += minuteStep) steps.push(m)
    return steps
  }, [minuteStep])

  // In 12h mode the draft hour is stored as 0..23; display as 1..12
  const displayHour = is12h ? (draftH24 % 12 === 0 ? 12 : draftH24 % 12) : draftH24

  // Arrow-key handler for a column
  const makeColKeyDown = useCallback(
    (
      items: number[],
      current: number,
      setCurrent: (v: number) => void,
    ) =>
      (e: ReactKeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          const idx = items.indexOf(current)
          setCurrent(items[(idx + 1) % items.length])
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          const idx = items.indexOf(current)
          setCurrent(items[(idx - 1 + items.length) % items.length])
        }
      },
    [],
  )

  // For 12h hour column selection — the "current" is displayHour (1..12)
  const setHour12 = useCallback((h12: number) => {
    // Preserve AM/PM when adjusting hour display
    const display = h12 === 12 ? 0 : h12
    setDraftH24(draftAmPm === 1 ? display + 12 : display)
  }, [draftAmPm])

  const setHour24 = useCallback((h24: number) => {
    setDraftH24(h24)
    setDraftAmPm(h24 >= 12 ? 1 : 0)
  }, [])

  const triggerClasses = [
    'dsDatePicker__trigger',
    size === 'sm' ? 'dsDatePicker__trigger--sm' : '',
    labelText ? '' : 'dsDatePicker__trigger--empty',
    open ? 'is-open' : '',
    className ?? '',
  ].filter(Boolean).join(' ')

  return (
    <>
      <Pressable
        ref={anchorRef}
        id={id}
        name={name}
        className={triggerClasses}
        style={style}
        disabled={disabled}
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel ?? resolvedPlaceholder}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={handleToggle}
      >
        <span className="dsDatePicker__triggerIcon" aria-hidden="true">
          <AccessTimeRoundedIcon fontSize="inherit" />
        </span>
        <OverflowTip label={triggerText}>
          <span className="dsDatePicker__triggerLabel">{triggerText}</span>
        </OverflowTip>
        {showClear ? (
          <span
            role="button"
            tabIndex={-1}
            className="dsDatePicker__triggerClear"
            onClick={handleClear}
            aria-label={getNlsString('NLSCommonTimePickerClear')}
          >
            <CloseRoundedIcon fontSize="inherit" />
          </span>
        ) : null}
      </Pressable>

      <FloatingMenu
        anchorRef={anchorRef}
        isOpen={open}
        onClose={handleClose}
        placement={placement}
        className="dsDatePicker__popover"
        role="dialog"
        ariaLabel={ariaLabel ?? resolvedPlaceholder}
      >
        {/* Time columns */}
        <div className="dsTime">
          {/* Hour column */}
          <div>
            <div className="dsTime__colHead">{getNlsString('NLSCommonTimePickerHour')}</div>
            <div
              className="dsTime__col"
              ref={hourColRef}
              role="listbox"
              aria-label={getNlsString('NLSCommonTimePickerHour')}
              tabIndex={0}
              onKeyDown={is12h
                ? makeColKeyDown(hours, displayHour, setHour12)
                : makeColKeyDown(hours, draftH24, setHour24)
              }
            >
              {hours.map((h) => {
                const isSelected = is12h ? h === displayHour : h === draftH24
                return (
                  <Pressable
                    key={h}
                    role="option"
                    tabIndex={-1}
                    className={`dsTime__cell${isSelected ? ' is-selected' : ''}`}
                    aria-selected={isSelected}
                    onClick={() => {
                      if (is12h) setHour12(h)
                      else setHour24(h)
                    }}
                  >
                    {pad2(h)}
                  </Pressable>
                )
              })}
            </div>
          </div>

          {/* Minute column */}
          <div>
            <div className="dsTime__colHead">{getNlsString('NLSCommonTimePickerMin')}</div>
            <div
              className="dsTime__col"
              ref={minColRef}
              role="listbox"
              aria-label={getNlsString('NLSCommonTimePickerMin')}
              tabIndex={0}
              onKeyDown={makeColKeyDown(minutes, draftM, setDraftM)}
            >
              {minutes.map((m) => (
                <Pressable
                  key={m}
                  role="option"
                  tabIndex={-1}
                  className={`dsTime__cell${m === draftM ? ' is-selected' : ''}`}
                  aria-selected={m === draftM}
                  onClick={() => setDraftM(m)}
                >
                  {pad2(m)}
                </Pressable>
              ))}
            </div>
          </div>

          {/* AM/PM column — only in 12h mode */}
          {is12h && (
            <div>
              <div className="dsTime__colHead">&nbsp;</div>
              <div
                className="dsTime__col"
                ref={ampmColRef}
                role="listbox"
                aria-label={getNlsString('NLSCommonTimePickerPeriod')}
                tabIndex={0}
                style={{ maskImage: 'none' }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault()
                    setDraftAmPm((prev) => (prev === 0 ? 1 : 0))
                    // Recalculate draftH24 preserving hour
                    const display = draftH24 % 12
                    const next: 0 | 1 = draftAmPm === 0 ? 1 : 0
                    setDraftH24(next === 1 ? display + 12 : display)
                  }
                }}
              >
                {([
                  { label: getNlsString('NLSCommonTimePickerAm'), val: 0 as const },
                  { label: getNlsString('NLSCommonTimePickerPm'), val: 1 as const },
                ] as Array<{ label: string; val: 0 | 1 }>).map(({ label, val }) => (
                  <Pressable
                    key={val}
                    role="option"
                    tabIndex={-1}
                    className={`dsTime__cell${draftAmPm === val ? ' is-selected' : ''}`}
                    aria-selected={draftAmPm === val}
                    style={{ minWidth: 0 }}
                    onClick={() => {
                      setDraftAmPm(val)
                      const display = draftH24 % 12
                      setDraftH24(val === 1 ? display + 12 : display)
                    }}
                  >
                    {label}
                  </Pressable>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="dsDatePicker__footer">
          <Button
            variant="tertiary"
            size="sm"
            onClick={handleNow}
          >
            {getNlsString('NLSCommonTimePickerNow')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleDone}
          >
            {getNlsString('NLSCommonTimePickerDone')}
          </Button>
        </div>
      </FloatingMenu>
    </>
  )
}
